import React, { useState } from 'react';
import { 
  Sparkles, 
  ChevronDown, 
  ChevronUp, 
  Copy, 
  RefreshCw,
  BookOpen,
  AlertCircle,
  FileText,
  TrendingUp,
  Target,
  Lightbulb
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { toast } from 'sonner';
import api from '../../lib/api';
import { SafeHTML } from '@/lib/sanitize';
import logger from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * NarrativeCard - Inline collapsible card for AI-generated analysis narratives
 * 
 * @param {Object} results - The calculation results object
 * @param {Object} configuration - The calculation configuration
 * @param {string} analysisType - 'single' | 'portfolio' | 'matrix'
 * @param {string} measureName - Name of the LEI measure(s)
 * @param {string} kpiNames - Names of the target KPI(s)
 * @param {string} currency - Currency code for formatting
 */
const NarrativeCard = ({ 
  results, 
  configuration, 
  analysisType = 'single',
  measureName = 'Selected Measure',
  kpiNames = 'Selected KPIs',
  currency = 'USD',
  onNarrativeGenerated = null // Callback to update parent/context
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const handleGenerateNarrative = async () => {
    setIsGenerating(true);
    try {
      const response = await api.post(`/generate-narrative`, {
        lei_measure_name: measureName,
        kpi_name: kpiNames,
        results,
        configuration,
        analysis_type: analysisType,
        include_sections: true, // Request both executive and technical sections
      });
      
      const generatedNarrative = response.data.narrative;
      setNarrative(generatedNarrative);
      setGeneratedAt(new Date().toLocaleString());
      setIsExpanded(true);
      
      // Update parent/context with the narrative so it gets saved with the scenario
      if (onNarrativeGenerated) {
        onNarrativeGenerated(generatedNarrative);
      }
      
      toast.success('Narrative generated successfully');
    } catch (error) {
      logger.error('Narrative generation error:', error);
      toast.error('Failed to generate narrative. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyNarrative = () => {
    if (narrative) {
      // Strip HTML tags for plain text copy
      const plainText = narrative.replace(/<[^>]*>/g, '').replace(/\n\n+/g, '\n\n');
      navigator.clipboard.writeText(plainText);
      toast.success('Narrative copied to clipboard');
    }
  };

  // Parse narrative into sections for better display
  const parseNarrativeSections = (text) => {
    if (!text) return null;
    
    const sections = {
      executive: '',
      methodology: '',
      recommendations: '',
      risks: ''
    };
    
    // Try to extract sections based on common headers
    const execMatch = text.match(/(?:#+\s*)?(?:EXECUTIVE SUMMARY|Executive Summary)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:METHODOLOGY|TECHNICAL|CAUSAL|RISK|RECOMMENDATION|$))/i);
    const methodMatch = text.match(/(?:#+\s*)?(?:METHODOLOGY|TECHNICAL BREAKDOWN|CAUSAL COMPONENTS|TECHNICAL)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:RISK|RECOMMENDATION|STRATEGIC|$))/i);
    const riskMatch = text.match(/(?:#+\s*)?(?:RISK|RISKS|RISK ANALYSIS|RISK CONSIDERATIONS)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:RECOMMENDATION|STRATEGIC|NEXT STEPS|$))/i);
    const recoMatch = text.match(/(?:#+\s*)?(?:RECOMMENDATION|STRATEGIC|NEXT STEPS|STRATEGIC RECOMMENDATIONS)[:\s]*([\s\S]*?)$/i);
    
    if (execMatch) sections.executive = cleanSectionText(execMatch[1]);
    if (methodMatch) sections.methodology = cleanSectionText(methodMatch[1]);
    if (riskMatch) sections.risks = cleanSectionText(riskMatch[1]);
    if (recoMatch) sections.recommendations = cleanSectionText(recoMatch[1]);
    
    // If no sections found, treat entire text as executive summary
    if (!sections.executive && !sections.methodology) {
      sections.executive = cleanSectionText(text);
    }
    
    return sections;
  };

  // Clean up section text by removing markdown artifacts
  const cleanSectionText = (text) => {
    if (!text) return '';
    return text
      // Remove markdown headers (##, ###, etc.)
      .replace(/^#+\s*/gm, '')
      // Remove standalone ## at start or end
      .replace(/^\s*##\s*$/gm, '')
      // Remove section headers that might be duplicated
      .replace(/^(?:EXECUTIVE SUMMARY|TECHNICAL BREAKDOWN|METHODOLOGY|RISK ANALYSIS|RISK CONSIDERATIONS|STRATEGIC RECOMMENDATIONS|RECOMMENDATIONS)[:\s]*/gim, '')
      // Remove lines that are just "s" or single characters (artifacts)
      .replace(/^\s*[a-z]\s*$/gm, '')
      // Clean up extra whitespace
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const sections = narrative ? parseNarrativeSections(narrative) : null;

  // Format text with basic markdown-like styling
  const formatText = (text) => {
    if (!text) return '';
    return text
      // Remove any remaining markdown headers
      .replace(/^#+\s*(.*)$/gm, '$1')
      // Remove standalone ## or ###
      .replace(/^\s*#{1,6}\s*$/gm, '')
      // Convert **text** to bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert *text* to italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Convert newlines to <br />
      .replace(/\n/g, '<br />');
  };

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-indigo-50/30">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-purple-50/50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    Tell Me What This Means
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-purple-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-purple-400" />
                    )}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    {narrative 
                      ? 'AI-generated analysis with executive summary and technical breakdown'
                      : 'Generate an AI-powered narrative explaining your results'
                    }
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {narrative && (
                  <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                    Generated
                  </Badge>
                )}
                {!isExpanded && !narrative && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateNarrative();
                    }}
                    disabled={isGenerating}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-3 w-3" />
                        Generate
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {/* Save reminder notice */}
            <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> This narrative will only be saved when you save this calculation as a scenario. 
                Use the "Save Scenario" button to preserve your analysis and narrative.
              </p>
            </div>

            {narrative ? (
              <div className="space-y-4">
                {/* Action buttons */}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">
                    Generated: {generatedAt}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyNarrative}
                    >
                      <Copy className="mr-2 h-3 w-3" />
                      Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateNarrative}
                      disabled={isGenerating}
                    >
                      <RefreshCw className={`mr-2 h-3 w-3 ${isGenerating ? 'animate-spin' : ''}`} />
                      Regenerate
                    </Button>
                  </div>
                </div>

                {/* Narrative sections */}
                <div className="space-y-4">
                  {/* Executive Summary */}
                  {sections?.executive && (
                    <div className="p-4 bg-white rounded-lg border border-purple-100">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-purple-600" />
                        <h4 className="font-semibold text-slate-800">Executive Summary</h4>
                      </div>
                      <SafeHTML 
                        className="text-sm text-slate-600 prose prose-sm max-w-none"
                        html={formatText(sections.executive)}
                      />
                    </div>
                  )}

                  {/* Technical/Methodology Breakdown */}
                  {sections?.methodology && (
                    <div className="p-4 bg-white rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-blue-600" />
                        <h4 className="font-semibold text-slate-800">Technical Breakdown</h4>
                      </div>
                      <SafeHTML 
                        className="text-sm text-slate-600 prose prose-sm max-w-none"
                        html={formatText(sections.methodology)}
                      />
                    </div>
                  )}

                  {/* Risk Analysis */}
                  {sections?.risks && (
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4 text-amber-600" />
                        <h4 className="font-semibold text-slate-800">Risk Analysis</h4>
                      </div>
                      <SafeHTML 
                        className="text-sm text-slate-600 prose prose-sm max-w-none"
                        html={formatText(sections.risks)}
                      />
                    </div>
                  )}

                  {/* Recommendations */}
                  {sections?.recommendations && (
                    <div className="p-4 bg-white rounded-lg border border-emerald-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="h-4 w-4 text-emerald-600" />
                        <h4 className="font-semibold text-slate-800">Strategic Recommendations</h4>
                      </div>
                      <SafeHTML 
                        className="text-sm text-slate-600 prose prose-sm max-w-none"
                        html={formatText(sections.recommendations)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-purple-200 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-800 mb-2">
                  Understand Your Results
                </h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">
                  Generate an AI-powered narrative that explains your calculation results 
                  with an executive summary, technical breakdown, and strategic recommendations.
                </p>
                <Button
                  onClick={handleGenerateNarrative}
                  disabled={isGenerating}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Generating Narrative...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Narrative
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default NarrativeCard;
